import React from 'react';
import { ArcSocketContext } from '@src/core/socket';
import { useFlightProvider } from '@src/features/order/provider';
import { getFlightPosition } from '@src/features/order/provider/flight-provider/flight-position-cache';
import { FlightEntity, PointEntity } from '@src/features/order/entities';
import { clearActiveGoHere } from '@src/features/drone/utils/go-here-registry';

/** Distance (m) within which the drone is treated as having reached the Go Here target. */
const GO_HERE_ARRIVAL_RADIUS_M = 12;

/** Max ground speed (m/s) to count as "arrived" — avoids clearing while still approaching. */
const GO_HERE_ARRIVAL_MAX_SPEED_MS = 4;

/** Consecutive in-radius polls required before clearing (guards GPS jitter / fly-through). */
const GO_HERE_ARRIVAL_CONFIRM_POLLS = 2;

/** Ignore arrival checks right after GOTO — avoids clearing on stale coords / instant GOTO SUCCESS. */
const GO_HERE_MIN_ACTIVE_MS = 3000;

/** Poll while a Go Here target is active so arrival is detected even if telemetry batches. */
const ARRIVAL_POLL_MS = 1000;

/** WebSocket COMMAND_STATUS commands that clear Go Here (not PAUSE — marker stays until Continue). */
const COMMANDS_THAT_CLEAR_GO_HERE = new Set([
  'CONTINUE',
  'START_MANUAL_CONTROL',
  'LAND',
  'RETURN',
  'DELIVERY_COMPLETED',
  'END_MISSION',
]);

type LatLng = { lat: number; lng: number };

type UseGoHereClearanceOptions = {
  resetGoHere: () => void;
  points: PointEntity[];
  serialNumber: string | null | undefined;
};

function distanceMeters(a: LatLng, b: LatLng): number {
  if (typeof google !== 'undefined' && google.maps?.geometry?.spherical) {
    return google.maps.geometry.spherical.computeDistanceBetween(
      new google.maps.LatLng(a.lat, a.lng),
      new google.maps.LatLng(b.lat, b.lng)
    );
  }

  const earthRadiusM = 6371000;
  const dLat = ((b.lat - a.lat) * Math.PI) / 180;
  const dLng = ((b.lng - a.lng) * Math.PI) / 180;
  const lat1 = (a.lat * Math.PI) / 180;
  const lat2 = (b.lat * Math.PI) / 180;
  const sinDLat = Math.sin(dLat / 2);
  const sinDLng = Math.sin(dLng / 2);
  const h =
    sinDLat * sinDLat +
    Math.cos(lat1) * Math.cos(lat2) * sinDLng * sinDLng;
  return 2 * earthRadiusM * Math.asin(Math.min(1, Math.sqrt(h)));
}

function getDroneCoordinates(
  effectiveSerial: string,
  flightData: FlightEntity | undefined
): LatLng | null {
  let lat: number | undefined;
  let lng: number | undefined;

  if (flightData?.serialNumber === effectiveSerial) {
    lat = Number(flightData.latitude);
    lng = Number(flightData.longitude);
  }

  if (!Number.isFinite(lat) || !Number.isFinite(lng)) {
    const cached = getFlightPosition(effectiveSerial);
    if (cached) {
      lat = Number(cached.latitude);
      lng = Number(cached.longitude);
    }
  }

  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
  return { lat: lat!, lng: lng! };
}

export function useGoHereClearance({
  resetGoHere,
  points,
  serialNumber,
}: UseGoHereClearanceOptions): void {
  const { flightData } = useFlightProvider();
  const { arcSocket, readyState } = React.useContext(ArcSocketContext);
  const pointsRef = React.useRef(points);
  const flightDataRef = React.useRef(flightData);
  const activeSinceRef = React.useRef(0);
  const nearTargetPollsRef = React.useRef(0);
  /** Set when operator presses Pause — blocks distance-based clear until Continue. */
  const userPausedGoHereRef = React.useRef(false);

  const effectiveSerial =
    serialNumber ?? (flightData?.serialNumber as string | undefined);

  pointsRef.current = points;
  flightDataRef.current = flightData;

  const activeTargetId = points[0]?.id;

  React.useEffect(() => {
    if (activeTargetId == null) return;
    activeSinceRef.current = Date.now();
    nearTargetPollsRef.current = 0;
    userPausedGoHereRef.current = false;
  }, [activeTargetId]);

  const clearGoHere = React.useCallback(() => {
    if (pointsRef.current.length === 0) return;
    nearTargetPollsRef.current = 0;
    userPausedGoHereRef.current = false;
    resetGoHere();
  }, [resetGoHere]);

  const tryClearOnArrival = React.useCallback(() => {
    const activePoints = pointsRef.current;
    const target = activePoints[0]?.position;
    if (!target || !effectiveSerial || activePoints.length === 0) return;
    if (userPausedGoHereRef.current) return;
    if (Date.now() - activeSinceRef.current < GO_HERE_MIN_ACTIVE_MS) return;

    const coords = getDroneCoordinates(effectiveSerial, flightDataRef.current);
    if (!coords) return;

    const distanceM = distanceMeters(coords, target);
    const speedMs = Number(flightDataRef.current?.speed);
    const withinRadius = distanceM <= GO_HERE_ARRIVAL_RADIUS_M;
    const slowEnough =
      !Number.isFinite(speedMs) || speedMs <= GO_HERE_ARRIVAL_MAX_SPEED_MS;

    if (withinRadius && slowEnough) {
      nearTargetPollsRef.current += 1;
      if (nearTargetPollsRef.current >= GO_HERE_ARRIVAL_CONFIRM_POLLS) {
        clearGoHere();
      }
      return;
    }

    nearTargetPollsRef.current = 0;
  }, [effectiveSerial, clearGoHere]);

  React.useEffect(() => {
    tryClearOnArrival();
  }, [flightData, activeTargetId, effectiveSerial, tryClearOnArrival]);

  React.useEffect(() => {
    if (activeTargetId == null || !effectiveSerial) return;
    const intervalId = window.setInterval(tryClearOnArrival, ARRIVAL_POLL_MS);
    return () => window.clearInterval(intervalId);
  }, [activeTargetId, effectiveSerial, tryClearOnArrival]);

  React.useEffect(() => {
    const ws = arcSocket();
    if (!ws || !effectiveSerial) return;

    const onMessage = (event: MessageEvent) => {
      if (pointsRef.current.length === 0) return;

      try {
        const data = JSON.parse(event.data ?? '{}') as {
          type?: string;
          status?: string;
          command?: string;
          serialNumber?: string;
        };

        if (data.type !== 'COMMAND_STATUS') return;

        const status = String(data.status ?? '').toUpperCase();
        if (status !== 'SUCCESS') return;

        if (data.serialNumber && data.serialNumber !== effectiveSerial) return;

        const command = String(data.command ?? '').toUpperCase();

        if (command === 'PAUSE') {
          userPausedGoHereRef.current = true;
          return;
        }

        if (COMMANDS_THAT_CLEAR_GO_HERE.has(command)) {
          clearGoHere();
        }
      } catch {
        // ignore malformed websocket payloads
      }
    };

    ws.addEventListener('message', onMessage);
    return () => ws.removeEventListener('message', onMessage);
  }, [arcSocket, readyState, effectiveSerial, clearGoHere, tryClearOnArrival]);
}

export function clearGoHereFromCameraSource(
  resetGoHere?: () => void,
  serialNumber?: string | null
): void {
  if (resetGoHere) {
    resetGoHere();
    return;
  }
  clearActiveGoHere(serialNumber);
}
